import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { buildCompanyUrl } from '../utils/navigation';

const Settings: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'settings' });
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const {
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessCompanyManagement,
    canAccessPpeSettings,
    canAccessSettings
  } = useRolePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (userRolePermissionsLoading) return;

    if (!canAccessSettings) {
      // No access to settings at all
      return;
    }

    // Determine the first available sub-page
    const settingsPath = (path: string) => currentCompany ? buildCompanyUrl(currentCompany.company_id, path) : `/${path}`;

    if (canManageRoles) {
      navigate(settingsPath('settings/roles'), { replace: true });
    } else if (canAccessUserManagement) {
      navigate(settingsPath('settings/users'), { replace: true });
    } else if (canAssignUsers) {
      navigate(settingsPath('settings/assignments'), { replace: true });
    } else if (canAccessCompanyManagement) {
      navigate(settingsPath('settings/companies'), { replace: true });
    } else if (canAccessPpeSettings) {
      navigate(settingsPath('settings/ppe-settings'), { replace: true });
    }
  }, [
    userRolePermissionsLoading,
    canAccessSettings,
    canManageRoles,
    canAccessUserManagement,
    canAssignUsers,
    canAccessCompanyManagement,
    canAccessPpeSettings,
    currentCompany,
    navigate
  ]);

  return (
    <Layout title={t('title')}>
      <Container className="an-enterprise-page an-settings-page an-page-shell">
        <div className="an-settings-hub-card text-center" role="status" aria-live="polite">
          <div className="an-settings-status-icon mx-auto">
            <i className="bi bi-gear-wide-connected" aria-hidden="true"></i>
          </div>
          {userRolePermissionsLoading ? (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('loading')}</p>
            </>
          ) : !canAccessSettings ? (
            <Alert variant="danger" role="alert" className="mb-0">
              {t('noAccess')}
            </Alert>
          ) : (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('redirecting')}</p>
            </>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default Settings;